<script setup lang="ts">
const { t: $t } = useI18n()

import * as Sentry from '@sentry/nuxt'

import type { KernelColor, KernelIcon } from '@medapp/kernel/types'

export interface Props {
  error: Error
  root?: boolean
  clearError?: () => void
}

const { error } = defineProps<Props>()

interface StatusProperties {
  title: string
  icon: KernelIcon
  color?: KernelColor
  message?: string | ((error: Error) => string)
}

watchEffect(() => {
  console.error(error)
})

const STATUS_MAP: Record<number, StatusProperties> = {
  400: {
    title: 'error.badRequest',
    icon: 'block-outline',
  },
  404: {
    title: 'error.title',
    message: $t('error.pageNotFound'),
    icon: 'frame-inspect-rounded',
  },
  418: {
    title: 'Teapot',
    message: '',
    icon: 'emoji-food-beverage-rounded',
  },
  500: {
    title: 'error.domainEvent',
    icon: 'error-rounded',
    color: 'signal-red-light',
  },
}

const statusCode = computed(() => {
  if (isNuxtError(error)) {
    return error.status ?? 500
  }

  return 500
})

const status = computed<StatusProperties>(() => {
  return {
    message: $t('error.defaultMessage'),
    ...STATUS_MAP[statusCode.value],
  }
})

const errorMessage = computed(() => {
  if (typeof status.value.message === 'function') {
    return status.value.message(error)
  }

  return status.value.message
})

const json = computed(() => {
  try {
    return JSON.stringify(error, null, 2)
  }
  catch {
    return error || '<circular data>'
  }
})

const { user } = useUser()
const route = useRoute()

const sentryId = ref(Sentry.lastEventId())

onMounted(async () => {
  await nextTick()
  sentryId.value = Sentry.lastEventId()
})
</script>

<template>
  <KernelEmptyState
    :title="$t(status.title)"
    class="error-state"
    :class="{ root }"
  >
    <KernelIcon
      v-if="status"
      :name="status.icon"
      :color="status.color"
    />

    <p>{{ errorMessage }}</p>

    <details>
      <summary>Voor nerds</summary>
      <h4>Info:</h4>
      <pre>
<code>SentryID:&#9;{{ sentryId }}
UserID:&#9;&#9;{{ user?.uid }}
Path:&#9;&#9;{{ route.fullPath }}</code></pre>

      <h4>Error:</h4>
      <pre><code>{{ json }}</code></pre>

      <h4>Trace:</h4>
      <pre v-if="error.stack"><code>{{ error.stack }}</code></pre>
      <small v-else>(none)</small>
    </details>
    <KernelButtonGroup>
      <KernelLinkButton
        v-if="root"
        to="/"
        type="secondary"
      >
        {{ $t('error.goBackHome') }}
      </KernelLinkButton>
      <KernelButton
        v-if="clearError && statusCode !== 404"
        @click="clearError()"
      >
        {{ $t('error.tryAgain') }}
      </KernelButton>
    </KernelButtonGroup>
  </KernelEmptyState>
</template>

<style scoped>
.error-state {
  padding-block: 1rem;

  &.root {
    .button-group {
      inline-size: auto;
    }

    details:open {
      background-color: var(--kernel-surface);
      box-shadow: var(--kernel-elevation-1);
    }
  }

  .button-group {
    inline-size: 100%;
  }

  details {
    font-size: 0.75rem;
    min-inline-size: 30rem;
    max-inline-size: 100%;
    text-align: start;
    transition: all 250ms ease;
    padding-inline: 0.5rem;
    margin-block: 0.5rem;
    overflow: auto;

    &:open {
      padding: 0.5rem;
      border-radius: var(--kernel-border-radius);
      background: var(--kernel-shade-100);
      color: contrast-color(var(--kernel-surface));

      summary {
        border-block-end: 1px solid var(--kernel-shade-200);
        margin-block-end: 1rem;
        padding-block-end: 0.5rem;
      }
    }

    pre {
      padding-inline-start: 1rem;
      line-height: 1.75;
    }

    h4 {
      margin-block-start: 0.5em;
    }

    summary {
      font: var(--kernel-font-body-2);
      text-align: center;
      cursor: pointer;
      border-radius: var(--kernel-border-radius);
      transition: all 250ms ease;

      &:focus-visible {
        outline: none;
        background-color: var(--kernel-shade-100);
      }
    }
  }
}
</style>
